'use client'

import { Suspense, useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { usePostHog } from '@posthog/next'
import { isQueryOnlyNavigation } from '@/lib/analytics-pageview'

/**
 * Fires `$pageview` for navigation that changes ONLY the query string.
 *
 * posthog-js owns every other case and this deliberately stays out of its way:
 *
 * | Navigation                | Captured by |
 * | ------------------------- | ----------- |
 * | First load                | posthog-js `_captureInitialPageview()` |
 * | Pathname change           | posthog-js `HistoryAutocapture` |
 * | Query-string-only change  | **this component** |
 *
 * `HistoryAutocapture._capturePageview()` compares `location.pathname` alone (verified in
 * posthog-js@1.369.3), so `/app/settings?tab=project` → `?tab=billing` emits nothing. 19 files
 * in this app drive state through `nuqs` — settings tabs, improvements tabs, accuracy filters,
 * prompt detail — so without this those views are invisible in pageview analytics.
 *
 * The obvious alternative — `capture_pageview: false` plus `<PostHogPageView />` — does not
 * work here: `opt_out_capturing()` calls `_captureInitialPageview()` **ungated** by
 * `capture_pageview` in its `cookieless_mode: 'on_reject'` branch, so every cookieless visitor
 * (the default population) would get two `$pageview`s on load. That is the exact double-fire
 * this replaced.
 */
function SearchParamsPageviewTracker() {
  const posthog = usePostHog()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const previous = useRef<{ pathname: string; search: string } | null>(null)

  useEffect(() => {
    const search = searchParams.toString()
    const prev = previous.current
    previous.current = { pathname, search }

    if (!isQueryOnlyNavigation(prev, { pathname, search })) return

    posthog?.capture('$pageview')
  }, [posthog, pathname, searchParams])

  return null
}

/**
 * `useSearchParams()` opts its subtree into client rendering, so the Suspense boundary is
 * required — without it the whole route tree it sits in would be forced dynamic.
 */
export function SearchParamsPageview() {
  return (
    <Suspense fallback={null}>
      <SearchParamsPageviewTracker />
    </Suspense>
  )
}
